import React, { useState, useEffect, useMemo } from 'react'
import { Container } from '@sainsburys-tech/grid'
import { ThemeProvider } from '@sainsburys-tech/theme-provider'
import { QueryClient, QueryClientProvider } from 'react-query'
import HeaderElement from '../components/Header'
import Hero from '../components/Hero'
import { Brands, getBrandFromHost } from '../data/brands'
import './global.css'

// Define the BrandKey type explicitly based on error message
type BrandKey =
  | 'sainsburys'
  | 'argos'
  | 'tu'
  | 'global'
  | 'nectar'
  | 'habitat'
  | 'sainsburyseco'
  | 'nectar360'

interface LayoutProps {
  pageTitle?: string
  location: Location
  children?: React.ReactNode
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})

const Layout: React.FC<LayoutProps> = ({ children, location }) => {
  const { pathname, host } = location

  const [theme, setTheme] = useState<BrandKey>(getBrandFromHost('') as BrandKey)

  useEffect(() => {
    setTheme(getBrandFromHost(host) as BrandKey)
  }, [host])

  const brand = useMemo(
    () => Brands.find((brand) => brand.key === theme),
    [theme]
  )

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider brand={theme}>
        <HeaderElement
          pathname={pathname}
          logo={brand?.logo}
          brand={brand?.key as BrandKey}
        />
        <Hero img={brand?.background} />
        <Container>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              // Only pass props that the child component can accept
              // You might need to modify this based on what props the children actually need
              const childProps = {} as any

              if (
                (typeof child.type === 'object' ||
                  typeof child.type === 'function') &&
                child.type !== null &&
                'brand' in (child.type as any)
              ) {
                childProps.brand = brand?.key
              }

              return React.cloneElement(child, childProps)
            }
            return child
          })}
        </Container>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export const Header = () => (
  <>
    <title>Lettuce Know</title>
    <meta name="description" content="Lettuce Know Prize Draw" />
    {process.env.GATSBY_STAGE !== 'prod' && (
      <meta
        name="robots"
        content="noindex, nofollow"
        data-test-id="meta-noindex"
      />
    )}
  </>
)

export default Layout
